import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { useLocation } from 'react-router-dom';

const navItems = [
  { id: 'hero-area', label: 'Início' },
  { id: 'products', label: 'Nossos produtos' },
  { id: 'services', label: 'Serviços' },
  { id: 'history', label: 'História' },
  { id: 'clients', label: 'Clientes' },
  { id: 'contact', label: 'Contato' },
];

function Header() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 992);
  const [activeSection, setActiveSection] = useState(isHomePage ? 'hero-area' : '');
  const [hoveredNavItem, setHoveredNavItem] = useState('');

  const sectionIds = useMemo(() => navItems.map((item) => item.id), []);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 992;
      setIsDesktop(desktop);
      if (desktop) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', isMobileMenuOpen && !isDesktop);
    return () => document.body.classList.remove('menu-open');
  }, [isDesktop, isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 24);

      if (!isHomePage) {
        return;
      }

      const offset = 140;
      let currentSection = 'hero-area';

      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (!element) {
          continue;
        }

        if (element.offsetTop - offset <= scrollTop) {
          currentSection = id;
        }
      }

      setActiveSection(currentSection);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage, sectionIds]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveSection(isHomePage ? 'hero-area' : '');
    setHoveredNavItem('');
  }, [isHomePage, location.pathname]);

  const buildHref = (id) => (isHomePage ? `#${id}` : `/#${id}`);

  const baseNavLinkStyle = {
    color: '#2D4A5E',
    fontWeight: 600,
    padding: '12px 18px',
    margin: '6px 4px',
    borderRadius: '9999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.25s ease',
    fontSize: '0.95rem',
  };

  return (
    <header>
      <motion.nav
        className={`navbar fixed-top scrolling-navbar ${isScrolled ? 'top-nav-collapse' : 'indigo'}`}
        initial={{ y: -96 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        style={{
          background: isScrolled ? 'rgba(255, 255, 255, 0.97)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderBottom: '1px solid rgba(59, 158, 212, 0.12)',
          boxShadow: isScrolled ? '0 10px 30px rgba(59, 158, 212, 0.12)' : '0 6px 20px rgba(59, 158, 212, 0.05)',
        }}
      >
        <div className="container navbar-container" style={{ minHeight: '76px' }}>
          <div className="navbar-header" style={{ width: 'auto' }}>
            <a
              href="/"
              className="navbar-brand"
              aria-label="Ir para a página inicial da Ecosistemas"
              style={{ position: 'static', padding: 0 }}
            >
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }} style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/assets/brandlogo.png"
                  alt="Ecosistemas Computação"
                  title="Ecosistemas Computação"
                  className="brand-logo-img"
                  style={{ height: '54px', width: 'auto', maxWidth: '260px' }}
                />
              </motion.div>
            </a>
          </div>

          {isDesktop ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <ul
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '4px',
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                }}
              >
                {navItems.map((item, index) => {
                  const isActive = isHomePage && activeSection === item.id;
                  const isHovered = hoveredNavItem === item.id;

                  return (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, y: -12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      style={{ listStyle: 'none' }}
                    >
                      <a
                        href={buildHref(item.id)}
                        aria-current={isActive ? 'page' : undefined}
                        onMouseEnter={() => setHoveredNavItem(item.id)}
                        onMouseLeave={() => setHoveredNavItem('')}
                        style={{
                          ...baseNavLinkStyle,
                          transform: !isActive && isHovered ? 'translateY(-1px)' : 'translateY(0)',
                          color: isActive || isHovered ? '#2178AB' : '#2D4A5E',
                          background: isActive || isHovered ? 'rgba(59, 158, 212, 0.08)' : 'transparent',
                          boxShadow: isActive || isHovered ? 'inset 0 0 0 1px rgba(59, 158, 212, 0.10)' : 'none',
                        }}
                      >
                        {item.label}
                      </a>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <button
              type="button"
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #3B9ED4 0%, #6BBDDF 100%)',
                borderRadius: '14px',
                color: '#fff',
                boxShadow: '0 10px 24px rgba(59, 158, 212, 0.22)',
              }}
            >
              {isMobileMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
            </button>
          )}
        </div>

        <AnimatePresence>
          {!isDesktop && isMobileMenuOpen && (
            <motion.div
              id="mobile-navigation"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                overflow: 'hidden',
                borderTop: '1px solid rgba(59, 158, 212, 0.12)',
                background: 'rgba(255, 255, 255, 0.98)',
              }}
            >
              <ul
                style={{
                  display: 'grid',
                  gap: '6px',
                  padding: '16px 20px 20px',
                  margin: 0,
                }}
              >
                {navItems.map((item) => {
                  const isActive = isHomePage && activeSection === item.id;

                  return (
                    <li key={item.id}>
                      <a
                        href={buildHref(item.id)}
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-current={isActive ? 'page' : undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 16px',
                          borderRadius: '16px',
                          background: isActive ? 'rgba(59, 158, 212, 0.10)' : 'rgba(235, 246, 252, 0.58)',
                          color: isActive ? '#2178AB' : '#3A5568',
                          fontWeight: 600,
                        }}
                      >
                        <span>{item.label}</span>
                        <span style={{ color: '#6BBDDF' }}>↗</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {isHomePage && (
        <section
          id="hero-area"
          className="hero-area-bg"
          style={{
            background: 'linear-gradient(180deg, #D6EEF8 0%, #EBF6FC 52%, #FAFEFF 100%)',
            paddingTop: '104px',
            paddingBottom: '20px',
          }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <motion.div
                className="col-lg-9 col-md-11 col-sm-12"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.12 }}
                style={{ textAlign: 'center', paddingBottom: '10px' }}
              >
                <h1
                  style={{
                    color: '#1A2D3A',
                    fontSize: 'clamp(2.5rem, 5vw, 4.2rem)',
                    fontWeight: 800,
                    lineHeight: 1.14,
                    letterSpacing: '-0.03em',
                    marginBottom: '20px',
                    maxWidth: 'none',
                  }}
                >
                  <span
                    style={{
                      background: 'linear-gradient(135deg, #3B9ED4 0%, #6BBDDF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Inovação
                  </span>{' '}
                  para o seu Negócio
                </h1>

                <p
                  style={{
                    color: '#4A6680',
                    fontSize: 'clamp(1rem, 2vw, 1.18rem)',
                    maxWidth: '760px',
                    lineHeight: 1.72,
                    margin: '0 auto 34px',
                  }}
                >
                  Soluções robustas em software para otimizar os processos, elevar a produtividade e impulsionar os resultados da sua empresa no mercado moderno.
                </p>

                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '14px', marginBottom: '28px' }}>
                  <motion.a
                    href="#products"
                    whileHover={{ y: -2, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      background: 'linear-gradient(135deg, #3B9ED4 0%, #6BBDDF 100%)',
                      color: '#fff',
                      padding: '18px 44px',
                      borderRadius: '9999px',
                      fontWeight: 700,
                      boxShadow: '0 14px 28px rgba(59, 158, 212, 0.20)',
                    }}
                  >
                    Conheça Nossos produtos
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}
    </header>
  );
}

export default Header;
